import { Outlet, useLocation, useNavigate } from 'react-router-dom'
import { useState, useEffect } from 'react'
import React from 'react'
import {
  LayoutDashboard,
  BookOpen,
  Tag,
  Video,
  Users,
  Settings,
  CreditCard,
  Menu,
  LogOut,
  ChevronLeft,
  ChevronRight,
  MessageCircle,
  Gift,
  Search,
  Bell,
  Moon,
  Sun
} from 'lucide-react'

interface NavItem {
  name: string
  href: string
  icon: React.ComponentType<{ className?: string }>
}

const navigation: NavItem[] = [
  { name: 'Dashboard', href: '/admin', icon: LayoutDashboard },
  { name: 'Courses', href: '/admin/courses', icon: BookOpen },
  { name: 'Assign Course', href: '/admin/assign-course', icon: Gift },
  { name: 'Discussions', href: '/admin/discussions', icon: MessageCircle },
  { name: 'Live Sessions', href: '/admin/live-sessions', icon: Video },
  { name: 'Students', href: '/admin/students', icon: Users },
  { name: 'Instructors', href: '/admin/instructors', icon: Users },
  { name: 'Testimonials', href: '/admin/testimonials', icon: Tag },
  { name: 'Payments', href: '/admin/payments', icon: CreditCard },
  { name: 'Settings', href: '/admin/settings', icon: Settings },
]

export default function AdminLayout() {
  const location = useLocation()
  const navigate = useNavigate()
  const [sidebarOpen, setSidebarOpen] = useState(true)
  const [user, setUser] = useState<any>(null)
  const [isDarkMode, setIsDarkMode] = useState(false)
  const [isMobile, setIsMobile] = useState(false)

  useEffect(() => {
    const checkMobile = () => {
      const mobile = window.innerWidth < 768
      setIsMobile(mobile)
      if (mobile) setSidebarOpen(false)
    }

    // Initialize dark mode state from DOM or localStorage
    const isDark = document.documentElement.classList.contains('dark') ||
      localStorage.getItem('theme') === 'dark' ||
      (!('theme' in localStorage) && window.matchMedia('(prefers-color-scheme: dark)').matches)
    setIsDarkMode(isDark)

    // Ensure DOM is in sync
    if (isDark) {
      document.documentElement.classList.add('dark')
    } else {
      document.documentElement.classList.remove('dark')
    }

    checkMobile()
    window.addEventListener('resize', checkMobile)
    return () => window.removeEventListener('resize', checkMobile)
  }, [])

  // Check if user is authenticated and is admin
  useEffect(() => {
    const token = localStorage.getItem('token')
    const userString = localStorage.getItem('user')

    if (!token || !userString) {
      navigate('/user/login')
      return
    }

    const userData = JSON.parse(userString)
    setUser(userData)

    if (userData.role !== 'admin' && userData.role !== 'ADMIN') {
      navigate('/user/login')
      return
    }
  }, [navigate])

  const handleLogout = () => {
    localStorage.removeItem('token')
    localStorage.removeItem('user')
    navigate('/user/login')
  }

  const isActive = (path: string) => location.pathname === path

  const toggleTheme = () => {
    const newMode = !isDarkMode
    setIsDarkMode(newMode)
    if (newMode) {
      document.documentElement.classList.add('dark')
      localStorage.setItem('theme', 'dark')
    } else {
      document.documentElement.classList.remove('dark')
      localStorage.setItem('theme', 'light')
    }
  }

  return (
    <div className="min-h-screen transition-colors duration-300 bg-cream-100 dark:bg-dark-950">
      <div className="flex h-screen overflow-hidden relative">
        {/* Mobile Backdrop */}
        {isMobile && sidebarOpen && (
          <div
            className="fixed inset-0 bg-black/50 z-40 backdrop-blur-sm transition-opacity"
            onClick={() => setSidebarOpen(false)}
          />
        )}

        {/* Floating Sidebar */}
        <aside
          className={`fixed ${isMobile ? 'left-0 top-0 bottom-0 rounded-r-3xl rounded-l-none' : 'left-4 top-4 bottom-4 rounded-3xl'} z-50 flex flex-col bg-white/80 dark:bg-dark-900/80 backdrop-blur-xl border border-white/20 dark:border-dark-700/50 shadow-2xl transition-all duration-500 ease-out 
            ${isMobile
              ? (sidebarOpen ? 'translate-x-0 w-72' : '-translate-x-full w-72')
              : (sidebarOpen ? 'w-72' : 'w-20')
            }`}
        >
          {/* Logo Section */}
          <div className="flex items-center justify-between p-6 mb-2">
            <div className={`flex items-center gap-3 overflow-hidden transition-all duration-500 ${!sidebarOpen && 'justify-center w-full'}`}>
              <div className="relative flex-shrink-0">
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-rose-500 to-mauve-600 flex items-center justify-center shadow-lg shadow-rose-500/20">
                  <span className="text-white font-bold text-lg">SA</span>
                </div>
                <div className="absolute -bottom-1 -right-1 w-3 h-3 bg-green-500 border-2 border-white dark:border-dark-900 rounded-full"></div>
              </div>

              <div className={`flex flex-col transition-opacity duration-300 ${sidebarOpen ? 'opacity-100' : 'opacity-0 w-0 hidden'}`}>
                <h1 className="text-lg font-bold text-charcoal-900 dark:text-white whitespace-nowrap">
                  Sudha's Academy
                </h1>
                <p className="text-xs text-charcoal-500 dark:text-charcoal-400 font-medium">
                  Admin Portal
                </p>
              </div>
            </div>
          </div>

          {/* Navigation */}
          <div className="flex-1 overflow-y-auto px-4 space-y-2 scrollbar-hide">
            {navigation.map((item) => {
              const Icon = item.icon
              const active = isActive(item.href)

              return (
                <button
                  key={item.name}
                  onClick={() => navigate(item.href)}
                  className={`group relative flex items-center w-full p-3.5 rounded-2xl transition-all duration-300 ${active
                    ? 'bg-rose-500 text-white shadow-lg shadow-rose-500/25'
                    : 'text-charcoal-500 dark:text-charcoal-400 hover:bg-rose-50 dark:hover:bg-dark-800 hover:text-rose-600 dark:hover:text-rose-400'
                    } ${!sidebarOpen && 'justify-center'}`}
                >
                  <Icon className={`w-[22px] h-[22px] transition-transform duration-300 ${active ? 'scale-110' : 'group-hover:scale-110'}`} />

                  <span className={`ml-3 font-medium whitespace-nowrap transition-all duration-300 ${sidebarOpen ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-4 absolute left-14 hidden'
                    }`}>
                    {item.name}
                  </span>

                  {/* Tooltip for collapsed state */}
                  {!sidebarOpen && (
                    <div className="absolute left-full ml-4 px-3 py-1.5 bg-charcoal-900 dark:bg-white text-white dark:text-charcoal-900 text-xs font-medium rounded-lg opacity-0 group-hover:opacity-100 pointer-events-none transition-opacity duration-200 whitespace-nowrap z-50 shadow-xl">
                      {item.name}
                      <div className="absolute left-0 top-1/2 -translate-x-1 -translate-y-1/2 w-2 h-2 bg-charcoal-900 dark:bg-white rotate-45"></div>
                    </div>
                  )}
                </button>
              )
            })}
          </div>

          {/* Bottom Section */}
          <div className="p-4 mt-2 space-y-2">
            <button
              onClick={() => setSidebarOpen(!sidebarOpen)}
              className={`w-full flex items-center justify-center p-3 rounded-2xl text-charcoal-400 hover:bg-charcoal-50 dark:hover:bg-dark-800 transition-colors ${isMobile ? 'hidden' : ''}`}
            >
              {sidebarOpen ? <ChevronLeft size={20} /> : <ChevronRight size={20} />}
            </button>

            <div className={`bg-charcoal-50 dark:bg-dark-800/50 rounded-2xl p-3 flex items-center gap-3 transition-all duration-300 ${!sidebarOpen && 'justify-center'}`}>
              <div className="w-9 h-9 rounded-full bg-gradient-to-tr from-rose-400 to-orange-400 p-0.5">
                <div className="w-full h-full rounded-full bg-white dark:bg-dark-900 overflow-hidden">
                  {user?.avatar ? (
                    <img src={user.avatar} alt="User" className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-rose-500 font-bold text-sm">
                      {(user?.name || 'A')[0].toUpperCase()}
                    </div>
                  )}
                </div>
              </div>

              {sidebarOpen && (
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-charcoal-900 dark:text-white truncate">
                    {user?.name || 'Admin'}
                  </p>
                  <p className="text-xs text-charcoal-500 dark:text-charcoal-400 truncate">
                    Administrator
                  </p>
                </div>
              )}

              {sidebarOpen && (
                <button
                  onClick={handleLogout}
                  className="p-1.5 text-charcoal-400 hover:text-rose-500 transition-colors"
                  title="Logout"
                >
                  <LogOut size={18} />
                </button>
              )}
            </div>
          </div>
        </aside>

        {/* Main Content */}
        <main
          className={`flex-1 flex flex-col h-full transition-all duration-500 ease-out 
            ${isMobile ? 'ml-0 w-full' : (sidebarOpen ? 'ml-80' : 'ml-28')}
          `}
        >
          {/* Top Bar */}
          <header className="h-20 px-4 md:px-8 flex items-center justify-between">
            <div className="flex items-center gap-4">
              {isMobile && (
                <button
                  onClick={() => setSidebarOpen(true)}
                  className="p-2 -ml-2 text-charcoal-500 dark:text-charcoal-400 hover:bg-gray-100 dark:hover:bg-dark-800 rounded-xl transition-colors"
                >
                  <Menu size={24} />
                </button>
              )}
              <h2 className="text-xl md:text-2xl font-bold text-charcoal-900 dark:text-white truncate">
                {navigation.find(n => isActive(n.href))?.name || 'Dashboard'}
              </h2>
            </div>

            <div className="flex items-center gap-4">
              <div className="relative hidden md:block">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-charcoal-400" />
                <input
                  type="text"
                  placeholder="Search..."
                  className="pl-10 pr-4 py-2.5 bg-white dark:bg-dark-800/50 border-none rounded-xl text-sm text-charcoal-600 dark:text-charcoal-200 focus:ring-2 focus:ring-rose-500/20 w-64 transition-all"
                />
              </div>

              <button
                onClick={toggleTheme}
                className="p-2.5 bg-white dark:bg-dark-800/50 rounded-xl text-charcoal-500 dark:text-charcoal-400 hover:text-rose-500 dark:hover:text-rose-400 transition-colors shadow-sm"
              >
                {isDarkMode ? <Sun size={20} /> : <Moon size={20} />}
              </button>

              <button className="relative p-2.5 bg-white dark:bg-dark-800/50 rounded-xl text-charcoal-500 dark:text-charcoal-400 hover:text-rose-500 dark:hover:text-rose-400 transition-colors shadow-sm">
                <Bell size={20} />
                <span className="absolute top-2 right-2.5 w-2 h-2 bg-rose-500 rounded-full border-2 border-white dark:border-dark-800"></span>
              </button>
            </div>
          </header>

          {/* Page Content */}
          <div className="flex-1 overflow-y-auto px-4 md:px-8 pb-8 scrollbar-hide">
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  )
}